/**
 * @model AccountClosureModel
 * @description
 * Represents the data structure for managing the lifecycle of a business account closure request within the AquaCloud platform.
 * This model is central to the "Account Closure" feature, tracking the state of a business entity as it moves through
 * various stages of deactivation or closure.
 *
 * **Key Responsibilities:**
 * 1. **Identity**: Links the closure request to a specific `businessID`.
 * 2. **State Management**: Uses the `status` property to determine the current phase (e.g., Requested, Inactive, Closed).
 * 3. **Audit Trail**: Contains a `StateTrackModel` to record who performed what action and when.
 * 4. **Context**: Stores the `appName` to identify the source of the request.
 *
 * **Data Flow:**
 * - Created when a user initiates an account closure request.
 * - Updated by administrators or system processes as the request is reviewed, approved, or cancelled.
 * - Persisted in the backend database to maintain a history of account status changes.
 *
 * **Usage:**
 * Used in `AccountClosureService` and components handling account settings or administrative overrides.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class AccountClosureModel {

    /**
     * @type {string}
     * @description The unique identifier of the business entity requesting closure.
     */
    businessID: string;

    /**
     * @type {number}
     * @description Numeric code representing the current status of the closure request.
     * Common values might include:
     * - `0`: Active / No Request
     * - `1`: Closure Requested
     * - `2`: Inactivated / Suspended
     * - `3`: Permanently Closed
     * - `4`: Cancelled Request
     */
    status: number;

    /**
     * @type {StateTrackModel}
     * @description Detailed history of state changes, including timestamps and user IDs for each transition.
     */
    stateTrack: StateTrackModel;

    /**
     * @type {string}
     * @description The ID or Name of the user who last updated this record.
     */
    updatedBy: string;

    /**
     * @type {boolean}
     * @description Flag indicating if the record has been modified since creation.
     * @optional
     */
    updated?: boolean;

    /**
     * @type {string}
     * @description The name of the application from which the closure was initiated (e.g., 'AquaCloud Web', 'Mobile App').
     */
    appName: string;

    /**
     * @constructor
     * @description Initializes a new instance of the AccountClosureModel.
     */
    constructor() {

    }
}

/**
 * @model StateTrackModel
 * @description
 * A sub-model used within `AccountClosureModel` to provide a granular audit trail of the account closure process.
 * It captures the "Who" and "When" for every significant state transition.
 *
 * **Tracked Events:**
 * - **Closure Requested**: When the user first asks to close the account.
 * - **Activated**: When an account is reactivated (if applicable).
 * - **Inactivated**: When the account is temporarily suspended or marked inactive.
 * - **Cancel Requested**: When a closure request is withdrawn.
 * - **Closed**: When the account is permanently closed.
 */
export class StateTrackModel {
    /** @type {string} @description ISO date string or timestamp of when the closure was requested. @optional */
    closureRequestedOn?: string;

    /** @type {string} @description User ID of the person who requested the closure. @optional */
    closureRequestedBy?: string;

    /** @type {string} @description ISO date string or timestamp of when the account was activated/reactivated. @optional */
    activatedOn?: string;

    /** @type {string} @description User ID of the person who activated the account. @optional */
    activatedBy?: string;

    /** @type {string} @description ISO date string or timestamp of when the account was inactivated. @optional */
    inactivatedOn?: string;

    /** @type {string} @description User ID of the person who inactivated the account. @optional */
    inactivatedBy?: string;

    /** @type {string} @description ISO date string or timestamp of when a cancellation of the closure request occurred. @optional */
    cancelRequestedOn?: string;

    /** @type {string} @description User ID of the person who requested the cancellation. @optional */
    cancelRequestedBy?: string;

    /** @type {string} @description ISO date string or timestamp of when the account was permanently closed. @optional */
    closedOn?: string;

    /** @type {string} @description User ID of the person who finalized the closure. @optional */
    closedBy?: string;
}